import { Capacitor } from '@capacitor/core';
import { NativePurchases, PURCHASE_TYPE, type Transaction } from '@capgo/native-purchases';
import { isNative } from './native';

/**
 * The full game's one-time purchase (docs/MONETIZATION.md): StoreKit 2 on iOS, Play Billing on
 * Android, through @capgo/native-purchases (no third-party service: Apple and Google keep the
 * records). The web has no store: it links to the app's store page instead.
 *
 * Free codes are Apple's offer codes for this product, made in App Store Connect: redeemed in the
 * App Store, by link, or in the game (redeemCode), they arrive as an ordinary purchase.
 */

/** The non-consumable's product id, the same in App Store Connect and the Play Console. */
export const FULL_GAME_PRODUCT = 'com.jaycemeyer.lawnmower.fullgame';

/**
 * The app's Apple ID (App Store Connect > App Information > Apple ID), for the web build's link
 * to the App Store: `VITE_APP_STORE_ID` in `.env.production`, shown once `VITE_APP_STORE_LIVE` is
 * true (the page is a 404 until release). Until then the web says the app is coming.
 */
const APP_STORE_ID = (import.meta.env.VITE_APP_STORE_ID as string | undefined) || null;
const APP_STORE_LIVE = import.meta.env.VITE_APP_STORE_LIVE === 'true';

/** Google Play's link works once the app is published there: `VITE_PLAY_STORE_LIVE=true`. */
const PLAY_STORE_LIVE = import.meta.env.VITE_PLAY_STORE_LIVE === 'true';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.jaycemeyer.lawnmower';

/**
 * Where the web build sends a visitor for the app: Google Play on an Android phone, the App Store
 * everywhere else. `url` is null while that store's page isn't live yet.
 */
export function appStoreLink(): { store: string; url: string | null } {
  if (/Android/i.test(navigator.userAgent)) {
    return { store: 'Google Play', url: PLAY_STORE_LIVE ? PLAY_STORE_URL : null };
  }
  return {
    store: 'the App Store',
    url: APP_STORE_ID && APP_STORE_LIVE ? `https://apps.apple.com/app/id${APP_STORE_ID}` : null,
  };
}

/**
 * Dev only: `?storePreview` draws the app's store screens in the browser with a sample price (for
 * screenshots, e.g. the purchase's App Review screenshot). Nothing can be bought.
 */
const storePreview = (): boolean =>
  import.meta.env.DEV && new URLSearchParams(location.search).has('storePreview');

/** Whether this build can sell the full game (the native apps). */
export const canPurchase = (): boolean => isNative() || storePreview();

/** The store this build sells through, for messages ("Google Play can't be reached."). */
export const storeName = (): string =>
  Capacitor.getPlatform() === 'android' ? 'Google Play' : 'The App Store';

/** Whether this build can open Apple's code redemption sheet (iOS). */
export const canRedeemCodes = (): boolean =>
  (isNative() && Capacitor.getPlatform() === 'ios') || storePreview();

const owns = (list: readonly Transaction[]): boolean =>
  list.some((t) => t.productIdentifier === FULL_GAME_PRODUCT && !t.revocationDate);

/** A price as the store gives it: its own formatted string, and the number and currency. */
export interface StorePrice {
  text: string;
  amount: number;
  currency: string;
}

/** The store's price for the full game, in the player's currency, or null if it can't be had. */
export async function fullGamePrice(): Promise<StorePrice | null> {
  if (storePreview()) return { text: '$2.99', amount: 2.99, currency: 'USD' };
  if (!canPurchase()) return null;
  try {
    const { products } = await NativePurchases.getProducts({
      productIdentifiers: [FULL_GAME_PRODUCT],
      productType: PURCHASE_TYPE.INAPP,
    });
    const p = products.find((x) => x.identifier === FULL_GAME_PRODUCT);
    return p ? { text: p.priceString, amount: p.price, currency: p.currencyCode } : null;
  } catch {
    return null;
  }
}

export type PurchaseOutcome = 'bought' | 'cancelled' | 'failed';

/** Buys the full game (the store's own sheet asks the player). */
export async function buyFullGame(): Promise<PurchaseOutcome> {
  if (!canPurchase()) return 'failed';
  try {
    const t = await NativePurchases.purchaseProduct({
      productIdentifier: FULL_GAME_PRODUCT,
      productType: PURCHASE_TYPE.INAPP,
      quantity: 1,
    });
    return t.productIdentifier === FULL_GAME_PRODUCT ? 'bought' : 'failed';
  } catch (e) {
    return /cancel/i.test(e instanceof Error ? e.message : String(e)) ? 'cancelled' : 'failed';
  }
}

/**
 * Whether the store says this player owns the full game: null if it could not be asked
 * (offline, no store). `restore` first syncs with the store (Restore Purchases).
 */
export async function ownsFullGame(restore = false): Promise<boolean | null> {
  if (!canPurchase()) return null;
  try {
    if (restore) await NativePurchases.restorePurchases();
    const { purchases } = await NativePurchases.getPurchases({
      productType: PURCHASE_TYPE.INAPP,
      onlyCurrentEntitlements: true,
    });
    return owns(purchases);
  } catch {
    return null;
  }
}

/** Apple's sheet for redeeming an offer code (a free unlock from the owner). */
export async function redeemCode(): Promise<boolean> {
  if (!canRedeemCodes()) return false;
  try {
    await NativePurchases.presentOfferCodeRedeemSheet();
    return true;
  } catch {
    return false;
  }
}

/**
 * Calls `cb` when a purchase of the full game arrives while the game runs: a code redeemed in
 * the sheet or the App Store, Ask to Buy approved, a purchase on another device.
 */
export function onFullGamePurchased(cb: () => void): void {
  if (!canPurchase()) return;
  void NativePurchases.addListener('transactionUpdated', (t) => {
    if (t.productIdentifier === FULL_GAME_PRODUCT && !t.revocationDate) cb();
  });
}
